import React, { DragEvent, ChangeEvent, useEffect, useRef, ReactNode } from 'react';

import { DeepPartial, UseFormReset, Control, UseFormWatch, UseFormSetValue } from 'react-hook-form';

import { ReactComponent as TrashCanIcon } from 'assets/svg/trash-can.svg';
import { REQUIRED_MESSAGE } from 'constants/global';
import { UPLOAD_FILE_MAX_FILE_SIZE_ERROR, UPLOAD_FILE_WRONG_FILETYPE_ERROR } from 'constants/shared';
import { errorNotify } from 'helpers';
import { Option } from 'interfaces';
import { FileUploadEntry } from 'interfaces/external-share-exchange.interface';
import { SelectFormInput } from 'shared-components';
import Typography from 'shared-components/Typography';
import DragUploadArea from 'shared-components/upload-file-modal/DragUploadArea';

type UploadFileFormsProps = {
  allowedFileTypes: string[];
  maxFileSize: number;
  allowedFileHelpText: string;
  dragAreaTitle?: string;
  multiple?: boolean;
  isLoading?: boolean;
  initialFileValues?: DeepPartial<{ files: FileUploadEntry[] }>;
  documentTypeOptions: Option[];
  allowedFileTypeErrorMessage?: string;
  allowedFileSizeError?: ReactNode | string;
  onError?: (error: string | boolean) => void;
  reset: UseFormReset<any>;
  setValue: UseFormSetValue<any>;
  control: Control<any>;
  watch: UseFormWatch<any>;
  inNotEditable?: boolean;
  watchFieldNamePrefix?: string;
};
type WatchedFileData = {
  documentName?: string;
  size?: string;
  documentType?: string;
};

const UploadFileWithDocType = ({
  multiple = true,
  allowedFileTypes,
  maxFileSize,
  allowedFileHelpText,
  dragAreaTitle,
  isLoading,
  initialFileValues,
  allowedFileTypeErrorMessage,
  allowedFileSizeError,
  onError,
  reset,
  documentTypeOptions,
  watch,
  setValue,
  control,
  watchFieldNamePrefix,
}: UploadFileFormsProps) => {
  const fileInput = useRef<null | HTMLInputElement>(null);

  const watchFiles = watch(watchFieldNamePrefix || 'files') as FileUploadEntry[];

  const handleFile = (file: File) => {
    const { type: fileType, size: fileSize } = file;

    if (!allowedFileTypes.includes(fileType)) {
      errorNotify(allowedFileTypeErrorMessage || UPLOAD_FILE_WRONG_FILETYPE_ERROR);
      return;
    }

    if (fileSize > maxFileSize) {
      errorNotify(allowedFileSizeError || UPLOAD_FILE_MAX_FILE_SIZE_ERROR, {
        closeOnClick: false,
      });
      onError?.(true);
      return;
    }

    const fileSizeMB = (fileSize / 1000000).toPrecision(2);

    const newEntry = {
      documentName: file.name,
      size: fileSizeMB,
      documentType: '',
      file,
    };

    const current = watch('files') || [];

    setValue('files', [...current, newEntry]);
  };

  const removeAllFiles = () => {
    if (fileInput.current) {
      fileInput.current.value = '';
    }
  };

  const onDrop = (ev: DragEvent) => {
    ev.preventDefault();
    ev.stopPropagation();

    const { files } = ev.dataTransfer;

    if (files && files[0]) {
      handleFile(files[0]);
    }
  };

  const onClickBrowseFile = () => {
    if (isLoading) return;

    if (fileInput.current) {
      fileInput.current.click();
    }
  };

  const onSelectedFile = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files && files[0]) {
      handleFile(files[0]);
    }

    if (fileInput.current) {
      fileInput.current.value = '';
    }
  };

  const handleDeleteFile = (index: number) => {
    const current = watch('files') || [];
    const updated = current.filter((_: WatchedFileData, i: number) => i !== index);
    setValue('files', updated);
  };

  useEffect(() => {
    if (initialFileValues) {
      reset({ ...initialFileValues });
    } else {
      reset();
    }
    removeAllFiles();
  }, [initialFileValues, reset]);

  return (
    <div className='mt-6 mb-8'>
      {(!watchFiles.length || multiple) && (
        <DragUploadArea
          dragAreaTitle={dragAreaTitle}
          allowedFileHelpText={allowedFileHelpText}
          onDrop={onDrop}
          onClickBrowseFile={onClickBrowseFile}
          isLoading={isLoading}
        />
      )}

      {watchFiles.length > 0 && (
        <div className='upload-files'>
          {watchFiles.map((file, fileIndex) => (
            <div key={fileIndex} className='mt-2 grid grid-cols-2 items-center'>
              <div className='flex items-center'>
                <TrashCanIcon
                  onClick={() => handleDeleteFile(fileIndex)}
                  className='mr-2 cursor-pointer [&_path]:fill-blue-600'
                />
                <Typography className='text-grey-800' tag='span'>
                  {`${file.documentName.split('T')[0]} - ${file.size}mb`}
                </Typography>
              </div>
              <div>
                <SelectFormInput
                  placeholder='Document Type'
                  control={control as unknown as Control}
                  name={`${watchFieldNamePrefix || 'files'}.${fileIndex}.documentType` as string}
                  options={documentTypeOptions}
                  required
                  validation={{ required: REQUIRED_MESSAGE }}
                  className='max-w-[350px]'
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <input ref={fileInput} className='hidden' type='file' onChange={onSelectedFile} multiple={false} />
    </div>
  );
};

export default UploadFileWithDocType;
